import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import type { PmoReleaseQuarterSummary } from '../../../types/pmo-release.js';

export interface ReleaseSummaryCardProps {
  /** 当四半期のリリースサマリー */
  current: PmoReleaseQuarterSummary;
  /** 先四半期のリリースサマリー（比較用） */
  previous: PmoReleaseQuarterSummary;
}

/**
 * 当 Q と先 Q を並べる比較タイル。先 Q は比較対象なので控えめに出す。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business リリースサマリーの比較タイル（ラベル・当Q値・先Q値）を描画する
 * @graph-connects none
 */
function CompareStat({
  label,
  current,
  previous,
  previousLabel,
}: {
  label: string;
  current: string;
  previous: string;
  previousLabel: string;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
      <p className="text-xs text-[var(--color-text-muted)]">{label}</p>
      <p className="mt-1 font-mono text-lg font-bold tabular-nums text-[var(--color-text-primary)]">
        {current}
      </p>
      <p className="mt-1 font-mono text-xs tabular-nums text-[var(--color-text-muted)]">
        {previousLabel} {previous}
      </p>
    </div>
  );
}

/**
 * 当 Q のリリース実績カード。総リリース件数・平均実績 pt・100pt 以上の件数を先 Q と並べ、
 * 100pt 以上のリリースは明細まで出す。
 *
 * 100pt 以上の集計からは非定型の案件を除外している（対象は API 側の定数で管理）。
 * 黙って落とすと件数が合わない理由を画面から追えないので、除外した件名は脚注に出す。
 *
 * 平均実績 pt の分母は総リリース件数。実績 pt が未入力のリリースも 1 件として数えるため、
 * シートの「【KPI】100Pt以上のリリース」タブ（分母は pt 入力済み件数）とは値が変わる。
 * 画面には注記を出さないが、API は `averagePointsPerPointed` でシート側の分母の値も
 * 返しており、突き合わせが必要になったらそちらを使う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOリリース実績カード。当Q/先Qの総リリース件数・平均実績pt・100pt以上の件数を並べ、当Qの100pt以上リリースを明細表示し、集計から外した非定型案件を脚注に出す
 * @graph-connects CompareStat [calls] 当Q/先Q比較タイル描画
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects Badge [calls] 100pt以上件数バッジ描画
 */
export function ReleaseSummaryCard({ current, previous }: ReleaseSummaryCardProps) {
  const { t } = useTranslation();
  const quarterLabel = t('kpi.pmoDelivery.quarterLabel', {
    fiscalYear: current.quarter.fiscalYear,
    quarter: current.quarter.quarter,
  });
  const previousQuarterLabel = t('kpi.pmoDelivery.quarterLabel', {
    fiscalYear: previous.quarter.fiscalYear,
    quarter: previous.quarter.quarter,
  });

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={`${t('kpi.pmoDelivery.release.title')}（${quarterLabel}）`}
        description={t('kpi.pmoDelivery.release.description')}
        action={
          <Badge variant="info">
            {t('kpi.pmoDelivery.release.largeBadge', { count: current.largeReleaseCount })}
          </Badge>
        }
      />
      <CardContent>
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <CompareStat
            label={t('kpi.pmoDelivery.release.totalCount')}
            current={t('kpi.pmoDelivery.release.countValue', { count: current.totalCount })}
            previous={t('kpi.pmoDelivery.release.countValue', { count: previous.totalCount })}
            previousLabel={previousQuarterLabel}
          />
          <CompareStat
            label={t('kpi.pmoDelivery.release.averagePoints')}
            current={t('kpi.pmoDelivery.release.pointValue', {
              points: current.averagePoints.toFixed(1),
            })}
            previous={t('kpi.pmoDelivery.release.pointValue', {
              points: previous.averagePoints.toFixed(1),
            })}
            previousLabel={previousQuarterLabel}
          />
          <CompareStat
            label={t('kpi.pmoDelivery.release.largeCount')}
            current={t('kpi.pmoDelivery.release.countValue', { count: current.largeReleaseCount })}
            previous={t('kpi.pmoDelivery.release.countValue', {
              count: previous.largeReleaseCount,
            })}
            previousLabel={previousQuarterLabel}
          />
          <CompareStat
            label={t('kpi.pmoDelivery.release.largeAveragePoints')}
            current={t('kpi.pmoDelivery.release.pointValue', {
              points: current.largeReleaseAveragePoints.toFixed(1),
            })}
            previous={t('kpi.pmoDelivery.release.pointValue', {
              points: previous.largeReleaseAveragePoints.toFixed(1),
            })}
            previousLabel={previousQuarterLabel}
          />
        </div>

        <h3 className="mb-2 text-sm font-bold text-[var(--color-text-primary)]">
          {t('kpi.pmoDelivery.release.detailTitle', { quarter: quarterLabel })}
        </h3>
        {current.largeReleases.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">{t('status.noData')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[32rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs text-[var(--color-text-muted)]">
                  <th className="w-28 py-2 font-medium">
                    {t('kpi.pmoDelivery.release.columnDate')}
                  </th>
                  <th className="py-2 font-medium">{t('kpi.pmoDelivery.release.columnProject')}</th>
                  <th className="w-24 py-2 text-right font-medium">
                    {t('kpi.pmoDelivery.release.columnPoints')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {current.largeReleases.map((release) => (
                  <tr
                    key={`${release.issueKey}-${release.releaseDate}`}
                    className="border-b border-[var(--color-border)] last:border-0"
                  >
                    <td className="py-2 font-mono text-xs tabular-nums text-[var(--color-text-secondary)]">
                      {release.releaseDate}
                    </td>
                    <td className="py-2 text-[var(--color-text-primary)]">
                      <span className="font-mono text-xs text-[var(--color-text-muted)]">
                        {release.issueKey}
                      </span>{' '}
                      {release.projectName}
                    </td>
                    <td className="py-2 text-right font-mono tabular-nums text-[var(--color-text-primary)]">
                      {t('kpi.pmoDelivery.release.pointValue', { points: release.points })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {current.largeReleaseExclusions.length > 0 && (
          <p className="mt-3 text-xs text-[var(--color-text-muted)]">
            {t('kpi.pmoDelivery.release.exclusionNote')}
            {current.largeReleaseExclusions.map((release) => (
              <span key={release.issueKey} className="ml-1">
                {release.issueKey} {release.projectName}（
                {t('kpi.pmoDelivery.release.pointValue', { points: release.points })}）
              </span>
            ))}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
